import { Avatar, Card, CardBody, CardHeader, CardTitle, ProgressBar } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import { mockManagers } from '@/shared/mocks/managers.ts'
import { useStageOwners } from '../../data/usePipelinesSource.ts'

// Module-scope lookup maps — `mockManagers` is a static const, no point recomputing
// these on every render. Will be replaced by snapshot data from the engine.
const MOCK_MANAGER_COLORS = new Map(mockManagers.map((m) => [m.id, m.color]))
const MOCK_MANAGER_INITIALS = new Map(mockManagers.map((m) => [m.id, m.initials]))

interface ByOwnerProps {
  totalLeadsInStage: number
}

export function ByOwner({ totalLeadsInStage }: ByOwnerProps) {
  const { t } = useTranslation()
  const owners = useStageOwners()

  return (
    <Card className="rounded-xl border-border shadow-none">
      <CardHeader className="border-border/20 py-2.5">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
          {t('pages.pipelines.side.byOwner', { defaultValue: 'By owner' })}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2.5 py-3">
        {owners.length === 0 && (
          <span className="text-xs text-muted">
            {t('pages.pipelines.side.empty', { defaultValue: 'Nothing to show.' })}
          </span>
        )}
        {owners.map((o) => {
          const pct =
            totalLeadsInStage === 0 ? 0 : Math.round((o.leadCount / totalLeadsInStage) * 100)
          return (
            <div key={o.managerId} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Avatar
                  size="xs"
                  initials={
                    MOCK_MANAGER_INITIALS.get(o.managerId) ??
                    o.managerName.slice(0, 2).toUpperCase()
                  }
                  color={MOCK_MANAGER_COLORS.get(o.managerId)}
                  seed={o.managerId}
                  aria-label={o.managerName}
                />
                <span className="flex-1 truncate text-sm text-foreground">{o.managerName}</span>
                <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                  {o.leadCount}
                </span>
              </div>
              <ProgressBar value={pct} tone="primary" size="sm" />
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}
